/**
 * Typed wrappers around /api/people. apiFetch attaches the bearer + handles
 * single-flight refresh — these helpers should never be used with raw fetch.
 */
import type { Person, PersonWithCredential, SysRole, WorkloadPeriod } from "@shared/types";
import { apiFetch } from "./api";

export interface PersonInput {
  name: string;
  email: string;
  sysRole?: SysRole | null;
  emp: "full" | "part";
  wl: number;
  roles: string[];
  periods: WorkloadPeriod[];
  status?: "active" | "archived";
  excludeFromPlanning?: boolean;
}

export interface PersonPatchInput extends Partial<PersonInput> {}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore — body might not be JSON
    }
    const message = (body as { message?: string } | null)?.message ?? `api ${res.status}`;
    const err = new Error(message) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return (await res.json()) as T;
}

export async function listPeople(): Promise<Person[]> {
  const res = await apiFetch("/api/people");
  return readJson<Person[]>(res);
}

export async function getPerson(id: number): Promise<Person> {
  const res = await apiFetch(`/api/people/${id}`);
  return readJson<Person>(res);
}

export async function createPerson(input: PersonInput): Promise<PersonWithCredential> {
  const res = await apiFetch("/api/people", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson<PersonWithCredential>(res);
}

export async function updatePerson(
  id: number,
  patch: PersonPatchInput,
): Promise<PersonWithCredential> {
  const res = await apiFetch(`/api/people/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  return readJson<PersonWithCredential>(res);
}

export async function deletePerson(id: number): Promise<void> {
  const res = await apiFetch(`/api/people/${id}`, { method: "DELETE" });
  if (res.status === 204) return;
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // ignore
  }
  const code =
    body && typeof body === "object" && "code" in body
      ? String((body as { code?: unknown }).code)
      : undefined;
  const err = new Error(`api ${res.status}`) as Error & {
    status: number;
    body: unknown;
    code?: string;
  };
  err.status = res.status;
  err.body = body;
  if (code !== undefined) err.code = code;
  throw err;
}
